import { useParams } from 'react-router-dom';
import SEOHead from '../components/common/SEOHead';
import { useState, useEffect } from 'react';
import {
  EyeIcon,
  CalendarIcon,
  TagIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import CommentForm from '../components/comments/CommentForm';
import CommentsList from '../components/comments/CommentsList';
import ShareButtons from '../components/common/ShareButtons';
import { API_ENDPOINTS } from '../config/constants';

interface Post {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string;
  featured_image: string;
  images?: Array<{ url: string; alt: string; caption: string }>;
  status: string;
  view_count: number;
  created_at: string;
  published_at: string;
  updated_at: string;
  author_name: string;
  category_name: string;
  tags: Array<{ id: string; name: string }>;
  location_country: string;
  location_city: string;
  meta_title: string;
  meta_description: string;
}

const PostPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [commentRefresh, setCommentRefresh] = useState(0);

  useEffect(() => {
    const fetchPost = async () => {
      if (!slug) return;

      try {
        const response = await fetch(`${API_ENDPOINTS.posts}/slug/${slug}`);
        const result = await response.json();

        if (result.success) {
          setPost(result.data);
        } else {
          setError('Post not found');
        }
      } catch (err) {
        setError('Failed to load post');
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="section-padding">
        <div className="container-max">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading post...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="section-padding">
        <div className="container-max">
          <div className="text-center">
            <h1 className="text-4xl font-display font-bold mb-4">
              Post Not Found
            </h1>
            <p className="text-gray-600">
              {error || 'The requested post could not be found.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const formattedDate = new Date(
    post.published_at || post.created_at
  ).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <>
      <SEOHead
        title={post.meta_title || post.title}
        description={post.meta_description || post.excerpt}
        path={`/blog/${post.slug}`}
        image={post.featured_image}
        type="article"
        publishedAt={post.published_at}
        updatedAt={post.updated_at}
        tags={post.tags?.map(t => t.name)}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: post.title,
          description: post.meta_description || post.excerpt,
          image: post.featured_image,
          datePublished: post.published_at,
          dateModified: post.updated_at,
          author: {
            '@type': 'Person',
            name: post.author_name,
          },
          publisher: {
            '@type': 'Organization',
            name: 'Mermaids Of The Moment',
          },
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `https://therehman.store/blog/${post.slug}`,
          },
        }}
      />

      <article className="section-padding">
        <div className="container-max">
          {/* Header */}
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-4xl md:text-5xl font-display font-bold mb-6 leading-tight">
                {post.title}
              </h1>

              {/* Meta info */}
              <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-600 mb-6">
                <div className="flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4" />
                  <span>{formattedDate}</span>
                </div>

                <div className="flex items-center gap-2">
                  <EyeIcon className="w-4 h-4" />
                  <span>{post.view_count} views</span>
                </div>

                {post.location_country && (
                  <div className="flex items-center gap-2">
                    <MapPinIcon className="w-4 h-4" />
                    <span>
                      {post.location_city && `${post.location_city}, `}
                      {post.location_country}
                    </span>
                  </div>
                )}

                {post.category_name && (
                  <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full">
                    {post.category_name}
                  </div>
                )}
              </div>

              {/* Tags */}
              {post.tags && post.tags.length > 0 && (
                <div className="flex items-center justify-center gap-2 mb-8">
                  <TagIcon className="w-4 h-4 text-gray-500" />
                  <div className="flex flex-wrap gap-2">
                    {post.tags.map(tag => (
                      <span
                        key={tag.id}
                        className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm"
                      >
                        {tag.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Featured Image */}
            {post.featured_image && (
              <div className="mb-8">
                <img
                  src={post.featured_image}
                  alt={post.title}
                  className="w-full h-auto object-contain rounded-lg shadow-lg max-h-screen"
                />
              </div>
            )}

            {/* Excerpt */}
            {post.excerpt && (
              <div className="mb-8">
                <p className="text-xl text-gray-700 italic text-center leading-relaxed">
                  {post.excerpt}
                </p>
              </div>
            )}

            {/* Share Buttons */}
            <ShareButtons
              url={`${window.location.origin}/blog/${post.slug}`}
              title={post.title}
              mediaUrl={post.featured_image}
              className="mb-10"
              hashtags={post.tags?.map(t => t.name) || []}
            />

            {/* Content */}
            <div className="prose prose-lg max-w-none">
              <div
                className="text-gray-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />
            </div>

            {/* Image Gallery */}
            {post.images && post.images.length > 0 && (
              <div className="mt-12">
                <h2 className="text-2xl font-display font-bold mb-6">
                  Photo Gallery
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {post.images.map((image, index) => (
                    <figure key={index} className="group">
                      <img
                        src={image.url}
                        alt={image.alt || `Photo ${index + 1}`}
                        className="w-full h-64 object-cover rounded-lg shadow-md"
                      />
                      {image.caption && (
                        <figcaption className="mt-2 text-sm text-gray-600 italic text-center">
                          {image.caption}
                        </figcaption>
                      )}
                    </figure>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </article>

      {/* Comments Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-max">
          <div className="max-w-4xl mx-auto space-y-8">
            {/* Existing Comments */}
            <CommentsList postId={post.id} refreshTrigger={commentRefresh} />

            {/* Comment Form */}
            <CommentForm
              postId={post.id}
              onCommentSubmitted={() => setCommentRefresh(prev => prev + 1)}
            />
          </div>
        </div>
      </section>
    </>
  );
};

export default PostPage;
